import React, { useState } from "react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { AuthModal } from "../components/AuthModal";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { BadgeCheck } from "lucide-react";

const PLANS = [
  { key: "traveler_daypass", name: "Traveler Day Pass", price: "$4.99", unit: "/day", desc: "Perfect for a quick trip.", features: ["24-hour full access", "Connect to any verified hotspot", "End-to-end encryption", "No commitment"] },
  { key: "neighbor_monthly", name: "Neighbor Monthly", price: "$19.99", unit: "/mo", popular: true, desc: "Unlimited everyday connectivity.", features: ["Unlimited connections", "Priority access to fast hotspots", "Member support", "Cancel anytime"] },
  { key: "host_pro_monthly", name: "Host Pro", price: "$9.99", unit: "/mo", desc: "For hosts who want to earn.", features: ["Keep 90% of earnings", "Unlimited hotspot listings", "Live guest manager", "Payout dashboard"] },
];

export default function Plans() {
  const { user } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [loading, setLoading] = useState("");

  const checkout = async (key) => {
    if (!user) { setAuthOpen(true); return; }
    setLoading(key);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setLoading("");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar onRequireAuth={() => setAuthOpen(true)} />
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-16 lg:py-24">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-3">Membership</p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mb-4">Choose your plan</h1>
          <p className="text-lg text-muted-foreground">Identity verification is required for every member. Cancel or change anytime.</p>
        </div>

        <div className="grid gap-5 md:grid-cols-3 max-w-5xl mx-auto">
          {PLANS.map((p) => (
            <div key={p.key} data-testid={`plan-${p.key}`}
              className={`rounded-2xl border p-8 flex flex-col ${p.popular ? "border-foreground bg-card shadow-xl" : "border-border bg-card/50"}`}>
              {p.popular && <span className="self-start font-mono text-xs uppercase tracking-wider px-2 py-1 rounded-full bg-foreground text-background mb-4">Most popular</span>}
              <h3 className="font-display text-xl font-medium">{p.name}</h3>
              <p className="text-sm text-muted-foreground mt-1">{p.desc}</p>
              <div className="mt-5 mb-6 flex items-baseline gap-1"><span className="font-display text-4xl font-bold">{p.price}</span><span className="text-sm text-muted-foreground">{p.unit}</span></div>
              <ul className="space-y-2.5 mb-8 flex-1">{p.features.map((f) => <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground"><BadgeCheck size={15} className="text-emerald-500 mt-0.5 shrink-0" /> {f}</li>)}</ul>
              <button onClick={() => checkout(p.key)} disabled={loading === p.key} data-testid={`checkout-${p.key}`}
                className={`h-12 rounded-xl text-sm font-medium transition-opacity hover:opacity-90 disabled:opacity-50 ${p.popular ? "bg-foreground text-background" : "border border-border"}`}>
                {loading === p.key ? "Redirecting…" : user ? "Subscribe" : "Sign in to subscribe"}
              </button>
            </div>
          ))}
        </div>
        <p className="text-center text-xs text-muted-foreground mt-10 font-mono">Secure checkout by Stripe · Test card 4242 4242 4242 4242</p>
      </section>
      <Footer />
      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} defaultMode="signin" redirectOnSuccess={false} />
    </div>
  );
}
